import { Language } from '../types';

export const aboutTranslations: Record<Language, Record<string, string>> = {
  en: {
    aboutTitle: "About Us",
    aboutTagline: "Being data driven is not about having reports. You are data driven when your data works for you!",

    // Mission + foundation section (EN + SK only)
    aboutMissionLead: "We bring order to scattered numbers, so you can build on them and make decisions with confidence.",
    aboutFoundationTitle: "We're not another platform. We're the foundation that makes your data work.",
    aboutFoundationText: "We connect your scattered data from every system you use, clean it and shape it so everything makes sense. And it doesn't end there: we stay with you. We keep a constant eye on data quality. When you need advice, we're here for consultations, and we'll train your team to work with the data on their own.",
    aboutPathsIntro: "What you build on top is up to you. We can show you the way or walk you through it:",
    aboutPathBuildTitle: "Want to build it yourself?",
    aboutPathBuildDesc: "You get clean, reliable data to build your own reports, automations and AI apps on. With consultations and training whenever you need them.",
    aboutPathRasTitle: "Want results right away?",
    aboutPathRasDesc: "Go for RAS, our ready-made analytics solution. Sales, margin, marketing and products are set up from day one, with no development on your side.",
    aboutStartTitle: "How do we start?",
    aboutStartText: "We look into your data to see where processes, measurement or testing cost you time or money, and propose a solution to your specific problem.",
    
    // Mission Section
    ourMission: "Our Mission",
    missionText: "Most of our clients had plenty of data but couldn't answer simple questions — what did we actually earn on that order, that campaign, that product? Their systems all told a different story. We connected them into one number they could trust.\n\nAnd that's just the start.",
    
    // Team Section
    ourTeam: "Who You'll Actually Work With",
    teamIntro: "The people behind Stratodata.",
    
    // Principles Section
    ourPrinciples: "Our Principles",
    principlesIntro: "Our operations are built on these core principles that guide everything we do.",
    
    principle1Title: "Personal Approach",
    principle1Desc: "We believe in understanding each client's unique needs and challenges to provide tailored solutions.",
    
    principle2Title: "Tailored Solution",
    principle2Desc: "We don't believe in one-size-fits-all. Each solution is custom-designed to meet specific business requirements.",
    
    principle3Title: "Technology & Consulting",
    principle3Desc: "We combine technology expertise with strategic consulting to maximize adoption of our solutions.",
    
    principle4Title: "Service is King",
    principle4Desc: "We prioritize exceptional service and support throughout our client relationships.",
    
    principle5Title: "Completeness of Vision",
    principle5Desc: "We aim for holistic solutions that address both current needs and future growth opportunities.",
    
    // CTA Section
    readyToWork: "Ready to Work With Us?",
    ctaDescription: "Let's discuss how our data solutions can transform your business operations and drive growth.",
    contactUsToday: "Grab 20 minutes with us",
    missionCtaText: "Pick our brains",
    missionCtaDescription: "No deck, no pitch — just a conversation about your data and where it could take you.",

    // SEO + mission tagline (EN + SK only; de/cs fall back to English)
    aboutSeoTitle: "About Stratodata",
    aboutSeoDescription: "Stratodata is a retail data engineering company. We build data warehouses, automation systems and AI-ready data foundations for e-commerce retailers across Europe.",
    aboutPutYour: "Put Your ",
    aboutDataToWork: "Data To Work",

    // Team members (EN + SK only)
    teamMfaRole: "CEO / Founder",
    teamMfaLine: "A report is just the beginning of the journey, not the end. We are not done until your data is put to work.",
    teamJkaRole: "CTO",
    teamJkaLine: "AI Analytics needs solid foundations. I lead our tech team and ensure your data architecture grows with you and answers your real questions.",
    teamEhaRole: "Data Engineer",
    teamEhaLine: "A great dashboard isn't magic - it's just well-built engineering. I turn messy data into reliable pipelines before it ever hits your screen.",
    teamMkoRole: "BI Developer",
    teamMkoLine: "If your team argues about whose numbers are right, that's where I come in. I build Power BI reports that give everyone one version of the truth.",
    teamKvrRole: "Data Engineer",
    teamKvrLine: "I bring all your business data together, so nobody has to piece together the big picture from 10 different spreadsheets.",
    teamMoRole: "Data Engineer",
    teamMoLine: "I help clients move their data from point A to point B, whether it's migrations, connectors or integrations.",
    teamDstRole: "Data Scientist / Engineer",
    teamDstLine: "Your data is probably scattered across five systems and a few spreadsheets. I bring it together in the cloud and build models that tell you what's coming next.",
    teamPkRole: "B2B Marketing Manager",
    teamPkLine: "Great data work deserves to be seen, ideally before your spreadsheet breaks. I make sure you hear about Stratodata and get what we do.",
  },
  de: {
    aboutTitle: "Über Uns",
    aboutTagline: "Datengetrieben zu sein bedeutet nicht, Berichte zu haben. Sie sind datengetrieben, wenn Ihre Daten für Sie arbeiten!",
    
    // Mission Section
    ourMission: "Unsere Mission",
    missionText: "Die meisten unserer Kunden hatten jede Menge Daten, konnten aber einfache Fragen nicht beantworten: Was haben wir an dieser Bestellung, dieser Kampagne, diesem Produkt tatsächlich verdient? Jedes ihrer Systeme erzählte eine andere Geschichte. Wir haben sie zu einer Zahl zusammengeführt, der sie vertrauen konnten.\n\nUnd das ist erst der Anfang.",
    
    // Team Section
    ourTeam: "Mit wem Sie tatsächlich arbeiten",
    teamIntro: "Die Menschen hinter Stratodata.",
    
    // Principles Section
    ourPrinciples: "Unsere Prinzipien",
    principlesIntro: "Unsere Operationen basieren auf diesen Kernprinzipien, die alles leiten, was wir tun.",
    
    principle1Title: "Persönlicher Ansatz",
    principle1Desc: "Wir glauben daran, die einzigartigen Bedürfnisse und Herausforderungen jedes Kunden zu verstehen, um maßgeschneiderte Lösungen anzubieten.",
    
    principle2Title: "Maßgeschneiderte Lösung",
    principle2Desc: "Wir glauben nicht an Einheitslösungen. Jede Lösung ist individuell gestaltet, um spezifische Geschäftsanforderungen zu erfüllen.",
    
    principle3Title: "Technologie & Beratung",
    principle3Desc: "Wir kombinieren technologisches Know-how mit strategischer Beratung, um die Adoption unserer Lösungen zu maximieren.",
    
    principle4Title: "Service ist König",
    principle4Desc: "Wir priorisieren außergewöhnlichen Service und Support in allen unseren Kundenbeziehungen.",
    
    principle5Title: "Vollständigkeit der Vision",
    principle5Desc: "Wir streben ganzheitliche Lösungen an, die sowohl aktuelle Bedürfnisse als auch zukünftige Wachstumschancen adressieren.",
    
    // CTA Section
    readyToWork: "Bereit, mit uns zusammenzuarbeiten?",
    ctaDescription: "Lassen Sie uns besprechen, wie unsere Datenlösungen Ihre Geschäftsprozesse transformieren und Wachstum fördern können.",
    contactUsToday: "20 Minuten mit uns reservieren",
    missionCtaText: "Unser Know-how anzapfen",
    missionCtaDescription: "Keine Folien, kein Pitch — nur ein Gespräch über Ihre Daten und wohin sie Sie bringen können.",
  },
  sk: {
    aboutTitle: "O nás",
    aboutTagline: "Byť dátovo riadený neznamená mať reporty. Dátovo riadení ste, keď dáta pracujú pre vás!",

    // Mission + foundation section
    aboutMissionLead: "Prinášame poriadok do roztrúsených čísel, aby ste nad nimi mohli stavať aj rozhodovať s istotou.",
    aboutFoundationTitle: "Nie sme ďalšia platforma. Sme základ, na ktorom vaše dáta fungujú.",
    aboutFoundationText: "Vaše roztrúsené dáta zo všetkých systémov, ktoré používate, spojíme, vyčistíme a upravíme tak, aby všetko dávalo zmysel. A tým to nekončí: zostávame s vami. Kvalitu dát priebežne strážime. Keď potrebujete poradiť, sme k dispozícii na konzultácie, a váš tím vyškolíme tak, aby s dátami vedel pracovať sám.",
    aboutPathsIntro: "Čo nad nimi postavíte, je na vás. Môžeme vám ukázať cestu alebo vás po nej previesť:",
    aboutPathBuildTitle: "Chcete si stavať sami?",
    aboutPathBuildDesc: "Dostanete čisté a spoľahlivé dáta, nad ktorými si vytvoríte vlastné reporty, automatizácie aj AI aplikácie. S konzultáciami a školeniami, keď ich budete potrebovať.",
    aboutPathRasTitle: "Chcete výsledky hneď?",
    aboutPathRasDesc: "Siahnite po RAS, našom hotovom analytickom riešení. Predaj, marža, marketing aj produkty sú v ňom pripravené od prvého dňa, bez vlastného vývoja.",
    aboutStartTitle: "Ako začneme?",
    aboutStartText: "Pozrieme sa do vašich dát, kde vám procesy, meranie alebo testovanie berú čas či peniaze, a navrhneme riešenie pre váš konkrétny problém.",
    
    // Mission Section
    ourMission: "Naša misia",
    missionText: "Väčšina našich klientov mala dát dosť, no nevedela odpovedať na jednoduché otázky: koľko sme na tej objednávke, kampani či produkte naozaj zarobili? Každý ich systém tvrdil niečo iné. Prepojili sme ich do jedného čísla, ktorému môžu veriť.\n\nA to je len začiatok.",
    
    // Team Section
    ourTeam: "S kým budete naozaj spolupracovať",
    teamIntro: "Stratodata team",
    
    // Principles Section
    ourPrinciples: "Naše princípy",
    principlesIntro: "Podľa týchto princípov robíme všetko.",
    
    principle1Title: "Osobný prístup",
    principle1Desc: "Začíname tým, že sa pýtame. Riešenie navrhujeme až vtedy, keď vieme, čo klient potrebuje a s čím zápasí.",
    
    principle2Title: "Riešenie na mieru",
    principle2Desc: "Jedno riešenie pre všetkých nerobíme. Každé navrhujeme podľa toho, čo konkrétna firma potrebuje.",
    
    principle3Title: "Technológie aj poradenstvo",
    principle3Desc: "Technológie spájame s poradenstvom, aby sa naše riešenia vo firme naozaj ujali a ľudia s nimi pracovali.",
    
    principle4Title: "Servis na prvom mieste",
    principle4Desc: "Počas celej spolupráce sme tu pre vás a záleží nám na tom, aby ste boli spokojní.",
    
    principle5Title: "Celistvá vízia",
    principle5Desc: "Riešenia navrhujeme tak, aby fungovali dnes a zvládli aj to, kam bude firma rásť.",
    
    // CTA Section
    readyToWork: "Chcete s nami spolupracovať?",
    ctaDescription: "Ukážeme vám, ako vám dáta môžu uľahčiť každodennú prácu a pomôcť firme rásť.",
    contactUsToday: "Rezervujte si s nami 20 minút",
    missionCtaText: "Porozprávajme sa o vašich dátach",
    missionCtaDescription: "Bez slajdov a predajných rečí. Len o tom, kam vás môžu posunúť.",

    // SEO + mission tagline
    aboutSeoTitle: "O Stratodata",
    aboutSeoDescription: "Stratodata robí dátové inžinierstvo pre retail. E-shopom a retailerom po celej Európe staviame dátové sklady, automatizácie a dátové základy, na ktorých môže stavať AI.",
    aboutPutYour: "Put Your ",
    aboutDataToWork: "Data To Work",

    // Team members
    teamMfaRole: "CEO / zakladateľ",
    teamMfaLine: "Report je len začiatok cesty, nie jej koniec. Hotovo máme až vtedy, keď vaše dáta naozaj pracujú.",
    teamJkaRole: "CTO",
    teamJkaLine: "AI analytika potrebuje pevné základy. Vediem náš technický tím a dbám na to, aby vaša dátová architektúra rástla s vami a odpovedala na otázky, ktoré naozaj riešite.",
    teamEhaRole: "Data Engineer",
    teamEhaLine: "Dobrý dashboard nie je žiadna mágia, len poctivá inžinierska práca. Z neporiadku v dátach robím spoľahlivé dátové toky skôr, než sa čokoľvek dostane na vašu obrazovku.",
    teamMkoRole: "BI Developer",
    teamMkoLine: "Keď sa váš tím háda, čie čísla sú správne, nastupujem ja. Robím reporty v Power BI, v ktorých všetci vidia tie isté čísla.",
    teamKvrRole: "Data Engineer",
    teamKvrLine: "Spájam všetky vaše firemné dáta, aby nikto nemusel skladať celkový obraz z 10 rôznych tabuliek.",
    teamMoRole: "Data Engineer",
    teamMoLine: "Pomáham klientom presunúť dáta z bodu A do bodu B, či už ide o migrácie, konektory alebo integrácie.",
    teamDstRole: "Data Scientist / Engineer",
    teamDstLine: "Vaše dáta sú asi roztrúsené v piatich systémoch a pár tabuľkách. Dávam ich dokopy v cloude a staviam modely, ktoré vám povedia, čo vás čaká.",
    teamPkRole: "B2B Marketing Manager",
    teamPkLine: "Dobrá práca s dátami si zaslúži, aby ju bolo vidieť. Ideálne skôr, než sa vám rozsype tabuľka. Mojou úlohou je, aby ste o Stratodata počuli a pochopili, čo robíme.",
  },
  cs: {
    aboutTitle: "O nás",
    aboutTagline: "Být datově orientovaný neznamená mít reporty. Jste datově orientovaní, když vaše data pracují pro vás!",
    
    // Mission Section
    ourMission: "Naše poslání",
    missionText: "Většina našich klientů měla dat dost, ale neuměla odpovědět na jednoduché otázky: kolik jsme doopravdy vydělali na téhle objednávce, téhle kampani, tomhle produktu? Každý jejich systém říkal něco jiného. Propojili jsme je do jednoho čísla, kterému mohli věřit.\n\nA to je teprve začátek.",
    
    // Team Section
    ourTeam: "S kým budete opravdu pracovat",
    teamIntro: "Lidé, kteří stojí za Stratodata.",
    
    // Principles Section
    ourPrinciples: "Naše principy",
    principlesIntro: "Naše operace jsou postaveny na těchto základních principech, které řídí vše, co děláme.",
    
    principle1Title: "Osobní přístup",
    principle1Desc: "Věříme v pochopení jedinečných potřeb a výzev každého klienta, abychom poskytli přizpůsobená řešení.",
    
    principle2Title: "Přizpůsobené řešení",
    principle2Desc: "Nevěříme v univerzální řešení. Každé řešení je navrženo na míru, aby splňovalo specifické obchodní požadavky.",
    
    principle3Title: "Technologie a konzultace",
    principle3Desc: "Kombinujeme technologické dovednosti se strategickými konzultacemi, abychom maximalizovali adopci našich řešení.",
    
    principle4Title: "Služba je král",
    principle4Desc: "Priorizujeme výjimečnou službu a podporu během celého vztahu s klientem.",
    
    principle5Title: "Úplnost vize",
    principle5Desc: "Usilujeme o komplexní řešení, která řeší současné potřeby i budoucí příležitosti růstu.",
    
    // CTA Section
    readyToWork: "Připraveni s námi spolupracovat?",
    ctaDescription: "Pojďme diskutovat o tom, jak mohou naše datová řešení transformovat vaše obchodní operace a podpořit růst.",
    contactUsToday: "Dejte si s námi 20 minut",
    missionCtaText: "Vytěžte z nás, co se dá",
    missionCtaDescription: "Žádná prezentace, žádný pitch — jen rozhovor o vašich datech a kam by vás mohla posunout.",
  }
};
